/**
 * StatsManager - Tracks all game statistics for achievements
 */

import type { Difficulty } from '../types';

const STORAGE_KEY = 'daynight_stats';

interface SessionStats {
    survivalTime: number;
    score: number;
    evolutions: number;
    devolves: number;
    shieldsUsed: number;
    heartsCollected: number;
    starsCollected: number;
    hitsTaken: number;
    noHitStreak: number;
    currentNoHitTime: number;
    difficulty: Difficulty;
    bigballMode: boolean;
    reachedMaxStage: boolean;
    survivedAfterDevolve: boolean;
    hasDevolved: boolean;
    bossDefeated: boolean;
}

interface SavedStats {
    totalDeaths?: number;
    totalPlaytime?: number;
    totalScore?: number;
    totalGamesPlayed?: number;
    totalEvolutions?: number;
    totalShieldsUsed?: number;
    totalHeartsCollected?: number;
    totalStarsCollected?: number;
}

export interface StatsForAchievements {
    totalDeaths: number;
    totalPlaytime: number;
    totalScore: number;
    totalGamesPlayed: number;
    totalEvolutions: number;
    totalShieldsUsed: number;
    totalHeartsCollected: number;
    totalStarsCollected: number;
    session: SessionStats;
}

export class StatsManager {
    // Cumulative stats (persisted to localStorage)
    private totalDeaths: number;
    private totalPlaytime: number;
    private totalScore: number;
    private totalGamesPlayed: number;
    private totalEvolutions: number;
    private totalShieldsUsed: number;
    private totalHeartsCollected: number;
    private totalStarsCollected: number;

    // Session stats (reset each game)
    public session: SessionStats;

    constructor() {
        // Cumulative stats (persisted to localStorage)
        this.totalDeaths = 0;
        this.totalPlaytime = 0;
        this.totalScore = 0;
        this.totalGamesPlayed = 0;
        this.totalEvolutions = 0;
        this.totalShieldsUsed = 0;
        this.totalHeartsCollected = 0;
        this.totalStarsCollected = 0;

        // Session stats (reset each game)
        this.session = this.createEmptySession();

        // Load saved stats
        this.loadStats();
    }

    createEmptySession(): SessionStats {
        return {
            survivalTime: 0,
            score: 0,
            evolutions: 0,
            devolves: 0,
            shieldsUsed: 0,
            heartsCollected: 0,
            starsCollected: 0,
            hitsTaken: 0,
            noHitStreak: 0,
            currentNoHitTime: 0,
            difficulty: 'normal',
            bigballMode: false,
            reachedMaxStage: false,
            survivedAfterDevolve: false,
            hasDevolved: false,
            bossDefeated: false
        };
    }

    loadStats(): void {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const data: SavedStats = JSON.parse(saved);
                this.totalDeaths = data.totalDeaths || 0;
                this.totalPlaytime = data.totalPlaytime || 0;
                this.totalScore = data.totalScore || 0;
                this.totalGamesPlayed = data.totalGamesPlayed || 0;
                this.totalEvolutions = data.totalEvolutions || 0;
                this.totalShieldsUsed = data.totalShieldsUsed || 0;
                this.totalHeartsCollected = data.totalHeartsCollected || 0;
                this.totalStarsCollected = data.totalStarsCollected || 0;
            }
        } catch (e) {
            console.warn('Could not load stats:', e);
        }
    }

    saveStats(): void {
        try {
            const data: SavedStats = {
                totalDeaths: this.totalDeaths,
                totalPlaytime: this.totalPlaytime,
                totalScore: this.totalScore,
                totalGamesPlayed: this.totalGamesPlayed,
                totalEvolutions: this.totalEvolutions,
                totalShieldsUsed: this.totalShieldsUsed,
                totalHeartsCollected: this.totalHeartsCollected,
                totalStarsCollected: this.totalStarsCollected
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        } catch (e) {
            console.warn('Could not save stats:', e);
        }
    }

    /**
     * Start a new game session
     */
    startSession(difficulty: Difficulty = 'normal', bigballMode: boolean = false): void {
        this.session = this.createEmptySession();
        this.session.difficulty = difficulty;
        this.session.bigballMode = bigballMode;
        this.totalGamesPlayed++;
    }

    /**
     * End the current game session - merge session stats to cumulative
     */
    endSession(): void {
        // Add session stats to cumulative
        this.totalDeaths++;
        this.totalPlaytime += this.session.survivalTime;
        this.totalScore += this.session.score;
        this.totalEvolutions += this.session.evolutions;
        this.totalShieldsUsed += this.session.shieldsUsed;
        this.totalHeartsCollected += this.session.heartsCollected;
        this.totalStarsCollected += this.session.starsCollected;

        // Save to localStorage
        this.saveStats();
    }

    /**
     * Update survival time and no-hit streak during gameplay
     */
    updateTime(deltaTime: number): void {
        this.session.survivalTime += deltaTime;
        this.session.score = Math.floor(this.session.survivalTime);
        this.session.currentNoHitTime += deltaTime;

        // Update best no-hit streak
        if (this.session.currentNoHitTime > this.session.noHitStreak) {
            this.session.noHitStreak = this.session.currentNoHitTime;
        }
    }

    /**
     * Record when player gets hit
     */
    recordHit(): void {
        this.session.hitsTaken++;
        this.session.currentNoHitTime = 0;
    }

    /**
     * Record evolution
     */
    recordEvolution(newStage: number): void {
        this.session.evolutions++;
        if (newStage >= 4) {
            this.session.reachedMaxStage = true;
        }
    }

    /**
     * Record devolution
     */
    recordDevolve(): void {
        this.session.devolves++;
        this.session.hasDevolved = true;
    }

    /**
     * Record survival after devolve (call this when player continues after devolving)
     */
    recordSurvivedAfterDevolve(): void {
        if (this.session.hasDevolved) {
            this.session.survivedAfterDevolve = true;
        }
    }

    /**
     * Record shield usage
     */
    recordShieldUse(): void {
        this.session.shieldsUsed++;
    }

    /**
     * Record heart collection
     */
    recordHeart(): void {
        this.session.heartsCollected++;
    }

    /**
     * Record star collection
     */
    recordStar(): void {
        this.session.starsCollected++;
    }

    /**
     * Record boss defeat
     */
    recordBossDefeat(): void {
        this.session.bossDefeated = true;
    }

    /**
     * Get all stats for achievement checking
     */
    getStatsForAchievements(): StatsForAchievements {
        return {
            // Cumulative
            totalDeaths: this.totalDeaths,
            totalPlaytime: this.totalPlaytime,
            totalScore: this.totalScore,
            totalGamesPlayed: this.totalGamesPlayed,
            totalEvolutions: this.totalEvolutions,
            totalShieldsUsed: this.totalShieldsUsed,
            totalHeartsCollected: this.totalHeartsCollected,
            totalStarsCollected: this.totalStarsCollected,
            // Session
            session: { ...this.session }
        };
    }
}
